import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { admin, ApiError, errorMessage } from "../lib/api";
import type { Domain, List, Store } from "../lib/types";
import { Alert, Badge, Button, Card, Field, PageHeader, Toggle } from "../components/ui";
import SeoFields, { type Seo } from "../components/SeoFields";

// SEO: tot ce ține de motoarele de căutare la nivel de magazin. Titlul și descrierea SEO ale produselor și
// categoriilor se editează în pagina fiecăruia.
export default function SeoPage() {
  return (
    <>
      <PageHeader title="SEO" subtitle="Cum apare magazinul în Google și ce poate indexa." />
      <div className="space-y-6">
        <Overview />
        <HomeSeo />
        <RobotsSettings />
      </div>
    </>
  );
}

function usePrimary() {
  const domains = useQuery({ queryKey: ["domains"], queryFn: () => admin.get<List<Domain>>("/domains") });
  const host = domains.data?.data.find((d) => d.is_primary)?.host ?? domains.data?.data[0]?.host ?? "";
  const scheme = location.protocol === "https:" ? "https" : "http";
  return { host, base: host ? `${scheme}://${host}` : "", count: domains.data?.data.length ?? 0 };
}

function Overview() {
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store") });
  const { host, base, count } = usePrimary();
  const hidden = !!(store.data?.settings.robots as { hide?: boolean } | undefined)?.hide;
  return (
    <Card title="Indexare">
      <dl className="grid gap-4 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-xs font-semibold text-slate-500">Stare</dt>
          <dd className="mt-1">{hidden ? <Badge tone="amber">ascuns de motoarele de căutare</Badge> : <Badge tone="green">indexabil</Badge>}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-slate-500">Domeniu principal</dt>
          <dd className="mt-1 font-mono">{host || "…"}</dd>
          <dd className="mt-0.5 text-xs text-slate-400">
            Adresele canonice și sitemap-ul folosesc acest domeniu.{count > 1 ? " " : " Adaugă un domeniu propriu din "}
            <Link to="/settings" className="text-brand underline">{count > 1 ? "Schimbă-l din Setări → Domenii" : "Setări → Domenii"}</Link>.
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-slate-500">Fișiere pentru motoarele de căutare</dt>
          <dd className="mt-1 space-x-3">
            {base && <a href={`${base}/sitemap.xml`} target="_blank" className="text-brand hover:underline">sitemap.xml ↗</a>}
            {base && <a href={`${base}/robots.txt`} target="_blank" className="text-brand hover:underline">robots.txt ↗</a>}
          </dd>
          <dd className="mt-0.5 text-xs text-slate-400">Trimite sitemap-ul în Google Search Console după ce verifici domeniul acolo.</dd>
        </div>
      </dl>
      <p className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500">
        Titlul și descrierea SEO ale fiecărui produs și ale fiecărei categorii se editează în{" "}
        <Link to="/products" className="text-brand underline">Produse</Link> și <Link to="/categories" className="text-brand underline">Categorii</Link>.
      </p>
    </Card>
  );
}

// Titlul și descrierea paginii principale (settings.seo); goale = numele magazinului și sloganul.
function HomeSeo() {
  const qc = useQueryClient();
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store") });
  const [seo, setSeo] = useState<Seo>({});
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  useEffect(() => { if (store.data) setSeo((store.data.settings.seo ?? {}) as Seo); }, [store.data]);
  const save = useMutation({
    mutationFn: () => admin.patch<Store>("/store", {
      settings: { ...(store.data?.settings ?? {}), seo: { title: seo.title?.trim() || undefined, description: seo.description?.trim() || undefined } },
    }),
    onSuccess: (s) => { qc.setQueryData(["store"], s); setMsg({ ok: true, text: "SEO-ul paginii principale a fost salvat." }); },
    onError: (e) => setMsg({ ok: false, text: errorMessage(e) }),
  });
  const s = store.data;
  return (
    <Card title="Pagina principală">
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <SeoFields value={seo} onChange={setSeo} fallbackTitle={s?.name ?? "Numele magazinului"}
          fallbackDescription={(s?.settings.tagline as string) || s?.name || ""} path="/" />
        <div className="flex items-center gap-3">
          <Button disabled={save.isPending}>Salvează</Button>
          {msg && <span className={`text-sm ${msg.ok ? "text-emerald-600" : "text-red-600"}`}>{msg.text}</span>}
        </div>
      </form>
    </Card>
  );
}

// Regulile implicite din robots.txt (aceleași ca în storefront: web/storefront/lib/seo.ts), doar pentru previzualizare.
const ROBOTS_DEFAULT = ["User-agent: *", "Disallow: /cart", "Disallow: /checkout", "Disallow: /account", "Disallow: /order/", "Disallow: /search", "Allow: /"];

// robots.txt: regulile platformei rămân mereu (paginile private, sitemap-ul); comerciantul adaugă reguli proprii
// sau ascunde temporar tot magazinul de motoarele de căutare.
function RobotsSettings() {
  const qc = useQueryClient();
  const store = useQuery({ queryKey: ["store"], queryFn: () => admin.get<Store>("/store") });
  const { host } = usePrimary();
  const [f, setF] = useState({ hide: false, extra: "" });
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  useEffect(() => {
    const r = (store.data?.settings.robots ?? {}) as { hide?: boolean; extra?: string };
    setF({ hide: !!r.hide, extra: r.extra ?? "" });
  }, [store.data]);
  const scheme = location.protocol === "https:" ? "https" : "http";
  const save = useMutation({
    mutationFn: () => admin.patch<Store>("/store", { settings: { ...(store.data?.settings ?? {}), robots: { hide: f.hide || undefined, extra: f.extra.trim() || undefined } } }),
    onSuccess: (s) => { qc.setQueryData(["store"], s); setMsg({ ok: true, text: "Setările pentru motoarele de căutare au fost salvate." }); },
    onError: (e) => {
      const fe = e instanceof ApiError ? e.problem.errors?.["settings.robots.extra"] : undefined;
      setMsg({ ok: false, text: fe ? `Reguli invalide: ${fe.replace(/^line (\d+):/, "linia $1:")}` : errorMessage(e) });
    },
  });
  const preview = f.hide
    ? "User-agent: *\nDisallow: /"
    : [...ROBOTS_DEFAULT, "", ...(f.extra.trim() ? [f.extra.trim(), ""] : []), `Sitemap: ${scheme}://${host || "…"}/sitemap.xml`].join("\n");
  return (
    <Card title="robots.txt" actions={host && <a href={`${scheme}://${host}/robots.txt`} target="_blank" className="text-xs font-semibold text-brand hover:underline">robots.txt publicat ↗</a>}>
      <form className="grid gap-5 lg:grid-cols-2" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <div className="space-y-4">
          <Toggle checked={f.hide} onChange={(hide) => setF({ ...f, hide })} label="Ascunde magazinul de motoarele de căutare" />
          {f.hide && (
            <Alert tone="amber">
              Nicio pagină a magazinului nu va fi indexată (robots.txt blochează tot și fiecare pagină are noindex). Util înainte
              de lansare; dezactivează opțiunea când magazinul e gata.
            </Alert>
          )}
          <Field label="Reguli suplimentare" hint="Se adaugă după regulile platformei. Directive: User-agent, Allow, Disallow, Crawl-delay, Sitemap; # pentru comentarii.">
            <textarea className="input min-h-40 font-mono text-xs" disabled={f.hide} spellCheck={false}
              placeholder={"# Exemplu: blochează un crawler anume\nUser-agent: GPTBot\nDisallow: /"} value={f.extra}
              onChange={(e) => setF({ ...f, extra: e.target.value })} />
          </Field>
          <div className="flex items-center gap-3">
            <Button disabled={save.isPending}>Salvează</Button>
            {msg && <span className={`text-sm ${msg.ok ? "text-emerald-600" : "text-red-600"}`}>{msg.text}</span>}
          </div>
        </div>
        <div>
          <div className="mb-1 text-xs font-semibold text-slate-600">Previzualizare robots.txt</div>
          <pre className="max-h-80 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-relaxed">{preview}</pre>
          <p className="mt-2 text-xs text-slate-400">
            Regulile platformei (coșul, checkout-ul, contul, comenzile și căutarea nu se indexează; sitemap-ul pe domeniul
            principal) rămân mereu.
          </p>
        </div>
      </form>
    </Card>
  );
}
